import {AfterViewInit, Component, Input, OnInit} from '@angular/core';
import {LoggerService} from "../../services/index";
import AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;

@Component({
  selector: 'app-google-maps-script',
  templateUrl: './google-maps-script.component.html',
})
export class GoogleMapsScriptComponent implements OnInit, AfterViewInit {

  // tslint:disable-next-line:variable-name
  _latitude: number | undefined;
  private map: any;
  private currentFeatureLayer: any;

  @Input('latitude') set latitude(value: any) {
    this._latitude = value;
    this.updateCentre();
  }

  get latitude() {
    return this._latitude;
  }

  // tslint:disable-next-line:variable-name
  _longitude: number | undefined;
  @Input('longitude') set longitude(value: any) {
    this._longitude = value;
    this.updateCentre();
  }

  get longitude() {
    return this._longitude;
  }

  // tslint:disable-next-line:variable-name
  _zoom: number | undefined;
  @Input('zoom') set zoom(value: any) {
    this._zoom = value;
    this.updateZoom();
  }

  get zoom() {
    return this._zoom;
  }

  @Input() streetViewControl = true;
  @Input() mapTypeControl = true;
  @Input() fullscreenControl = true;

  // tslint:disable-next-line:variable-name
  _markers: AdvancedMarkerElement[] | undefined;
  @Input('markers') set markers(value: any) {
    this._markers = value;
    this.updateMarkers();
  }

  get markers() {
    return this._markers;
  }

  // tslint:disable-next-line:variable-name
  _placeIds: string[] = [];
  @Input('placeIds') set placeIds(value: string[]) {
    // console.log('Setting placeIds:', value);
    this._placeIds = value;
    this.updateFeatureLayerStyling();
  }

  get placeIds() {
    return this._placeIds;
  }

  // tslint:disable-next-line:variable-name
  _featureType = "ADMINISTRATIVE_AREA_LEVEL_2";
  @Input('featureType') set featureType(value: string) {
    // console.log('Setting featureType:', value);
    this._featureType = value;
    // Initialize feature layer when featureType is set and map exists
    if (this.map) {
      this.initializeFeatureLayer();
    }
  }

  get featureType() {
    return this._featureType;
  }

  constructor(private logService: LoggerService) {
  }

  logger = this.logService.taggedLogger(this.constructor?.name);

  ngAfterViewInit() {

  }

  async ngOnInit() {
    // console.log('ngOnInit starting...');

    // @ts-ignore
    const [{Map, Markers}] = await Promise.all([
      google.maps.importLibrary("maps"),
      google.maps.importLibrary("marker")
    ]);

    // console.log('Google Maps libraries loaded');

    this.map = new Map(document.getElementById("map"), {
      mapId: "866f0268d4e1a46b",
      streetViewControl: this.streetViewControl,
      mapTypeControl: this.mapTypeControl,
      fullscreenControl: this.fullscreenControl,
      center: { lat: 52.5956081, lng: -0.2690179 }, // Peterborough
      zoom: 10
    });

    // console.log('Map initialized with ID: 866f0268d4e1a46b');

    this.updateCentre();
    this.updateZoom();
    this.updateMarkers();

    // Initialize feature layer now that map exists
    // console.log('Map ready, featureType is:', this.featureType);
    if (this.featureType) {
      // console.log('Calling initializeFeatureLayer with featureType:', this.featureType);
      this.initializeFeatureLayer();
    }

    // Add map idle listener to debug when map is ready
    this.map.addListener('idle', () => {
      // console.log('🗺️ Map is idle, zoom:', this.map.getZoom());
      // console.log('🗺️ Map center:', this.map.getCenter()?.toJSON());

      // Test if data-driven styling is working by trying to access any feature layer
      try {
        const testLayer = this.map.getFeatureLayer('ADMINISTRATIVE_AREA_LEVEL_2');
        // console.log('✅ ADMINISTRATIVE_AREA_LEVEL_2 layer accessible');

        // REMOVED the test style function that was interfering

      } catch (e) {
        console.error('❌ ADMINISTRATIVE_AREA_LEVEL_2 layer not accessible:', e);
      }
    });

    // Also add a tiles loaded listener
    this.map.addListener('tilesloaded', () => {
      // console.log('🔲 Map tiles loaded');
    });
  }

  private initializeFeatureLayer() {
    if (this.map != null && this.featureType) {
      // Clear existing feature layer styling if any
      if (this.currentFeatureLayer) {
        this.currentFeatureLayer.style = null;
      }

      try {
        // Get the new feature layer
        this.currentFeatureLayer = this.map.getFeatureLayer(this.featureType);
        // console.log('Feature layer initialized for type:', this.featureType);
        // console.log('Current placeIds:', this.placeIds);

        const featureStyleOptions = {
          strokeColor: "#4E93D1",
          strokeOpacity: 1.0,
          strokeWeight: 3.0,
          fillColor: "#4E93D1",
          fillOpacity: 0.5,
        };

        this.currentFeatureLayer.style = (options: any) => {
          // console.log('Style function called for feature:', options.feature.placeId);
          // console.log('Feature display name:', options.feature.displayName);
          // console.log('Looking for placeIds:', this.placeIds);

          if (this.placeIds && this.placeIds.includes(options.feature.placeId)) {
            // console.log('✓ STYLING feature:', options.feature.placeId);
            return featureStyleOptions;
          }

          // console.log('✗ Not styling feature:', options.feature.placeId);
          // Return null for features that shouldn't be styled
          return null;
        };

        // console.log('Feature layer style function set');
      } catch (error) {
        console.error('Error initializing feature layer:', error);
        console.log('Feature type:', this.featureType);
      }
    }
  }

  private updateFeatureLayerStyling() {
    if (this.currentFeatureLayer) {
      // Force a style refresh by reassigning the style function
      const featureStyleOptions = {
        strokeColor: "#4E93D1",
        strokeOpacity: 1.0,
        strokeWeight: 3.0,
        fillColor: "#4E93D1",
        fillOpacity: 0.5,
      };

      this.currentFeatureLayer.style = (options: any) => {
        if (this.placeIds.includes(options.feature.placeId)) {
          return featureStyleOptions;
        }
        return null;
      };
    }
  }

  private updateCentre() {
    if (this.map != null) {
      this.map.setCenter({lat: this.latitude ?? 52.33, lng: this.longitude ?? -1.940936});
    }
  }

  private updateZoom() {
    if (this.map != null) {
      this.map.setZoom(this.zoom ?? 6);
    }
  }

  private updateMarkers() {
    if (this.map != null && this.markers?.length > 0) {
      this.markers
        .filter((x: any) => x.position != null && x.position.lat != null && x.position.lng != null)
        .forEach((latlng: any) => {
          const marker = new google.maps.marker.AdvancedMarkerElement(
            {map: this.map, position: latlng.position}
          );
        });
    }
  }
}
